<template>
  <div tabindex="0" class="p-table" @keydown.tab="onSelectionNavigate($event, 1)" @keydown.up="onSelectionNavigate($event, -1)" @keydown.down="onSelectionNavigate($event, 1)">
    <table :class="classList">
      <caption v-if="caption">
        {{ caption }}
      </caption>
      <thead>
        <slot name="headers" :columns="computedColumns">
          <tr>
            <th v-if="multiSelect"></th>
            <th
              v-for="(column, index) in computedColumns"
              :key="'col-' + (column.value ? column.value : index)"
              :style="{
                width: column.width,
                'text-align': _.defaultTo(column.textAlign, 'left')
              }"
              @click="onHeaderClick($event, column)"
              @dblclick="onHeaderDoubleClick($event, column)"
            >
              {{ column.value }}
              <span v-if="canSort">
                <p-icon v-if="column.sort" dense right>
                  {{ column.sort.ascending ?'arrow_downward':'arrow_upward' }}
                </p-icon>
                <p-icon v-else dense right>
                  horizontal_rule
                </p-icon>
              </span>
            </th>
          </tr>
        </slot>
      </thead>
      <tbody>
        <tr v-if="loading" aria-hidden="true" class="p-table-loading-indicator-row">
          <td :colspan="computedColumns.length">
            <p-progress-linear :indeterminate="loading"></p-progress-linear>
          </td>
        </tr>
        <slot name="prepend-table"></slot>
        <tr
          v-if="computedRows.length === 0 && !loading && !_.component.hasSlot(this, 'rows')"
        >
          <td :colspan="computedColumns.length">
            <p-row justify-center>
              <slot name="no-items">
                {{ noDataText }}
              </slot>
            </p-row>
          </td>
        </tr>
        <slot name="rows">
          <slot v-for="(row, rowIndex) in computedRows" name="row" :row="row">
            <slot name="prepend-row" :row="row"></slot>
            <tr
              :key="(row[keyField] ? row[keyField].value : rowIndex)"
              :ref="'row-' + rowIndex"
              :class="{'p-table-row': true, 'selectable': canSelect && !multiSelect, 'selected': selectedRows.includes(row)}"
              tabindex="0"
              @click="onRowClick($event, row)"
              @dblclick="onRowDoubleClick($event, row)"
              @keydown.enter="onRowKeyEnter($event, row)"
            >
              <td v-if="multiSelect">
                <p-checkbox :value="selectedRows.includes(row)" @input="onCheckboxSelect($event, row)"></p-checkbox>
              </td>
              <template v-for="(column, colIndex) in computedColumns">
                <td
                  v-if="row[column.field] || !_.component.hasSlot(this, column.field)"
                  :key="'cell-' + colIndex"
                  :colspan="getColSpan(row[column.field], colIndex)"
                  :class="{['color-' + _.defaultTo(row[column.field], {}).color] : !_.isNil(_.defaultTo(row[column.field], {}).color)}"
                  :style="{'text-align': _.defaultTo(column.textAlign, 'left')}"
                  @click="onCellClick($event, row[column.field])"
                  @dblclick="onCellDoubleClick($event, row[column.field])"
                >
                  <slot
                    v-if="!row.editMode || column.editable === false"
                    :name="column.field"
                    :row="row"
                    :entry="row[column.field]"
                    :column="column"
                    :col-index="colIndex"
                    :row-index="rowIndex"
                  >
                    {{ formatValue(_.defaultTo(row[column.field], {}).value, column.format) }}
                  </slot>
                  <slot
                    v-else
                    :name="column.field + '-edit'"
                    :row="row"
                    :entry="row[column.field]"
                    :column="column"
                    :col-index="colIndex"
                    :row-index="rowIndex"
                  >
                    <template v-if="row[column.field]">
                      <p-text-field v-model="row[column.field].value" solo></p-text-field>
                    </template>
                  </slot>
                </td>
              </template>
            </tr>
            <slot name="append-row" :row="row"></slot>
          </slot>
        </slot>
      </tbody>
      <tfoot>
        <slot name="append-table"></slot>
      </tfoot>
    </table>
  </div>
</template>

<script lang="ts">
  import { PropType } from 'vue';
  import sizeable from '../../mixins/sizeable/sizeable';
  import hoverable from '../../mixins/hoverable/hoverable';
  import loadable from '../../mixins/loadable/loadable';
  import mixins from '../../mixins';
  import _ from '../../utils';
  import IpTableColumn from './IpTableColumn';
  import IpTableRow from './IpTableRow';

  export default mixins(sizeable, hoverable, loadable).extend({
    mixins: [sizeable, hoverable, loadable],
    props: {
      zebra: { default: true, type: Boolean },
      value: { default: undefined, type: Array as PropType<IpTableRow[] | undefined> },
      columns: { default: undefined, type: Array as PropType<IpTableColumn[] | undefined> },
      caption: { default: undefined, type: String },
      canSelect: { default: false, type: Boolean },
      canSort: { default: false, type: Boolean },
      multiSelect: { default: false, type: Boolean },
      selectionMandatory: { default: false, type: Boolean },
      keyField: { default: undefined, type: String },
    },
    data: () => ({
      selectedRows: [] as IpTableRow[],
    }),
    computed: {
      noDataText() {
        return this.$t('core.PTable.noData');
      },
      classList() {
        return {
          'p-table': true,
          dense: this.dense,
          spacious: this.spacious,
          hover: this.hover,
          zebra: this.zebra,
        };
      },
      computedColumns() {
        if (this.columns !== undefined) {
          return this.columns as [];
        }
        if (this.value === undefined || !Array.isArray(this.value) || this.value.length === 0) {
          return [];
        }

        const firstEntry = this.value[0] as any;
        return Object.keys(firstEntry).map((key) => ({
          field: key,
          value: key,
          sort: undefined,
        } as IpTableColumn));
      },
      computedRows() {
        const self = this as any;
        const rows = self.value as IpTableRow[];
        if (rows === undefined) {
          return [];
        }
        let neededInitalization = false;
        rows.forEach((row: IpTableRow, i: number) => {
          // eslint-disable-next-line no-underscore-dangle
          if (row._pTableInitalizedRow === true) {
            return;
          }
          neededInitalization = true;
          Object.keys(row).forEach((key) => {
            let entry = row[key];
            if (!entry || !_.isObject(entry) || entry.value === undefined) {
              row[key] = { value: entry };
              entry = row[key];
            }

            Object.defineProperty(entry, 'pTableInternalValue', {
              configurable: false,
              enumerable: false,
              writable: true,
              value: entry.value,
            });
            Object.defineProperty(entry, 'value', {
              get() {
                return entry.pTableInternalValue;
              },
              set(newValue) {
                // Force update if value is set, reactivity does not work otherwise
                // TODO: Find a way to make it reactive without having to forceUpdate
                self.$forceUpdate();
                entry.pTableInternalValue = newValue;
              },
            });
          });
          Object.defineProperty(row, 'index', {
            value: i,
            enumerable: false,
            writable: false,
            configurable: false,
          });
          Object.defineProperty(row, 'pTableInternalEditMode', {
            value: false,
            enumerable: false,
            writable: true,
            configurable: false,
          });
          Object.defineProperty(row, 'beforeEdit', {
            value: undefined,
            enumerable: false,
            writable: true,
            configurable: false,
          });
          Object.defineProperty(row, '_pTableInitalizedRow', {
            value: true,
            enumerable: true,
            writable: false,
            configurable: false,
          });
          Object.defineProperty(row, 'cancelEdit', {
            value() {
              // Get previous values but don't overwrite row & extra functionality
              Object.keys(row).forEach((rowKey) => {
                Object.keys(row[rowKey]).forEach((propertyKey) => {
                  // Create new instance by deep copy because otherwise references might be kept to the 'beforeEdit' nested object
                  (row as any)[rowKey][propertyKey] = _.deepClone(row.beforeEdit[rowKey][propertyKey]);
                });
              });
              row.editMode = false;
            },
            enumerable: false,
            writable: false,
            configurable: false,
          });

          Object.defineProperty(row, 'editMode', {
            enumerable: false,
            configurable: false,
            set(newValue) {
              if (row.pTableInternalEditMode === false && newValue === true) {
                (row as any).beforeEdit = {} as IpTableRow;
                Object.keys(row).forEach((key) => {
                  // Create new instance by deep copy, in case a nested object is present
                  // This way nested objects don't keep their reference
                  row.beforeEdit![key] = _.deepClone(row[key]);
                });
              }
              row.pTableInternalEditMode = newValue;
              self.$forceUpdate();
            },
            get() {
              return row.pTableInternalEditMode;
            },
          });
          Object.defineProperty(row, 'values', {
            get() {
              const values = {} as typeof row;
              const keys = Object.keys(row);
              keys.forEach((key) => {
                const entry = row[key];
                if (typeof (entry) === 'string') {
                  values[key] = entry;
                } else {
                  values[key] = entry.value;
                }
              });
              return values;
            },
            configurable: false,
            enumerable: false,
          });
          this.computedColumns.forEach((column) => {
            if (row[column.field] === undefined) {
              // To keep backwards compatibility each field that is defined in the columns should have a value
              row[column.field] = { value: undefined };
            }
          });
        });
        // In case of switching dynamically between single and multi
        if (!this.multiSelect && this.selectedRows.length > 1) {
          this.selectedRows = [this.selectedRows[0]];
        }
        if (this.selectionMandatory && this.selectedRows.length === 0) {
          const numRows = this.value?.length ?? 0;
          if (numRows > 0) {
            this.selectedRows = [rows[0]];
            this.$emit('selection-change', this.selectedRows);
          }
        }

        if (neededInitalization) {
          this.$emit('input', rows);
        }

        return rows;
      },
    },
    methods: {
      formatValue(value: any, formatter: IpTableColumn['format']) {
        if (typeof formatter !== 'function') {
          return value;
        }
        return formatter(value);
      },
      getColSpan(rowEntry: IpTableRow, index: number) {
        if (rowEntry === undefined) {
          return 1;
        }
        const { colspan } = rowEntry;
        if (colspan === 'full') {
          return this.computedColumns.length - index;
        }
        return colspan;
      },
      onHeaderClick(event: Event, value: IpTableColumn) {
        if (this.canSort) {
          // Ascending value on first click
          const ascendingInitial = true;
          const sort = value.sort ?? {} as IpTableColumn['sort'];
          const ascending = sort?.ascending;
          // Clear all sorting, would need to be changed for multisort
          this.computedColumns.forEach((column) => {
            column.sort = undefined;
          });
          if (_.isNil(ascending) || ascending === ascendingInitial) {
            const currentAscending = ascending ?? !ascendingInitial;
            const toggledAscending = !currentAscending;
            value.sort = {
              ascending: toggledAscending,
              field: value.field,
            };
          } else {
            // Third click
            value.sort = undefined;
          }
          this.$forceUpdate();
        }
        this.$emit('header-cell-click', value);
      },
      onHeaderDoubleClick(event: Event, value: any) {
        this.$emit('header-cell-double-click', value);
      },
      onCellClick(event: Event, value: any) {
        this.$emit('cell-click', value);
      },
      onCellDoubleClick(event: Event, value: any) {
        this.$emit('row-cell-double-click', value);
      },
      onRowClick(event: Event, value: any) {
        if (!this.multiSelect) {
          this.performSelection(event as KeyboardEvent, value);
        }
        this.$emit('row-click', value);
      },
      onRowDoubleClick(event: Event, value: any) {
        this.$emit('row-double-click', value);
      },
      onRowKeyEnter(event: Event, value: any) {
        this.$emit('row-key-enter', value);
      },
      onCheckboxSelect(event: Event, row: any) {
        this.performSelection((event as KeyboardEvent), row, true);
      },
      onSelectionNavigate(event: KeyboardEvent, offset: number) {
        event.preventDefault();
        if (this.selectedRows.length > 0) {
          const selection = this.selectedRows[this.selectedRows.length - 1];
          let newIndex = selection.index! + offset;
          newIndex = Math.max(0, newIndex);
          const numRows = this.value?.length ?? 0;
          newIndex = Math.min(numRows - 1, newIndex);
          const newSelection = (this.value as IpTableRow[])[newIndex];
          if ((event.ctrlKey || event.shiftKey) && this.multiSelect) {
            this.selectedRows.push(newSelection);
          } else {
            this.selectedRows = [newSelection];
          }
          const cellEl = (this.$refs as any)[`row-${newIndex}`][0];
          cellEl.focus();
        }
      },
      performSelection(event: KeyboardEvent, value: IpTableRow, isSelected?: boolean) {
        if (this.canSelect) {
          if (event.shiftKey && this.multiSelect && this.selectedRows.length > 0) {
            const lastSelected = this.selectedRows[this.selectedRows.length - 1];
            const maxIndex = Math.max(value.index!, lastSelected.index!);
            const minIndex = Math.min(value.index!, lastSelected.index!);
            const rows = this.value as IpTableRow[];
            for (let i = minIndex; i < maxIndex + 1; i += 1) {
              if (!this.selectedRows.includes(rows[i])) {
                this.selectedRows.push(rows[i]);
              }
            }
          } else if (this.multiSelect) {
            if (this.selectedRows.includes(value) || isSelected === false) {
              // If mandatory can not go below 1 selection
              if (!(this.selectionMandatory && this.selectedRows.length === 1)) {
                // Deselect
                this.selectedRows = this.selectedRows.filter((row) => row !== value);
              }
            } else {
              // Add to selection
              this.selectedRows.push(value);
            }
          } else if (!this.selectionMandatory && this.selectedRows.length === 1 && this.selectedRows.includes(value)) {
            // Deselect
            this.selectedRows = [];
          } else {
            // Select only one
            this.selectedRows = [value];
          }
          this.$emit('selection-change', this.selectedRows);
        }
      },
    },
  });
</script>
